/**
 * Campaign wizard state + reducer + step gating (SCRUM-209), ported from Ryan's
 * ccState. NOTE the deliberate key↔label swap (Ryan's CC_STEPS): the 'preview'
 * step's label is "Messaging" and the 'export' step's label is "Preview".
 *
 * SCRUM-497 (product-first targeting): the Audience step is REMOVED — a
 * deliberate, documented deviation from the v7 prototype (decision 2026-07-10:
 * the audience feature is no longer pursued). Targeting is now either a
 * CONTEXTUAL SEED (`targetIds` non-null — wizard opened from a referral, the
 * drawer, or a ledger selection) or PRODUCT-FIRST auto-targeting (`targetIds`
 * null — the target set is computed from the selected product's eligibility
 * rules; resolution lives in the wizard component, SCRUM-500).
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { eligibleTargets } from '../customer-eligibility';

import { generateEmail, generateTalkTrack, type TuneStyle } from './message-templates';

export interface WizardStep {
  key: 'product' | 'preview' | 'export';
  label: string;
}

export const CC_STEPS: readonly WizardStep[] = [
  { key: 'product', label: 'Product' },
  { key: 'preview', label: 'Messaging' },
  { key: 'export', label: 'Preview' },
];

export type PreviewTab = 'talk' | 'email';
export type ExportFormat = 'csv' | 'copy';

export interface CcState {
  step: number;
  /**
   * Contextual target seed: non-null when the wizard was opened FOR specific
   * customers (referral Start-outreach, drawer, ledger selection) — those ids
   * ARE the target set. Null means product-first auto-targeting: the target
   * set derives from the selected product's eligibility rules (SCRUM-500).
   */
  targetIds: string[] | null;
  productId: string | null;
  productPreSelected: boolean;
  campaignName: string;
  nameEditing: boolean;
  previewTab: PreviewTab;
  talkTrack: string;
  emailSubject: string;
  emailBody: string;
  talkVariant: number;
  emailVariant: number;
  tuneChips: { talk: string[]; email: string[] };
  generating: { talk: boolean; email: boolean };
  editing: { talk: boolean; email: boolean };
  exportFormat: ExportFormat;
  draftSaved: boolean;
}

export interface WizardInit {
  /** Contextual seed (see CcState.targetIds); omit/null for auto-targeting. */
  targetIds?: string[] | null;
  productId?: string | null;
  productPreSelected?: boolean;
  campaignName?: string;
  step?: number;
  /**
   * SCRUM-348: referral that opened this wizard (Start outreach / console
   * approve). On export completion the referral flips to campaign_sent —
   * the v6 linkedReferralId behavior, now server-persisted.
   */
  linkedReferralId?: string;
}

export function initCcState(init: WizardInit): CcState {
  return {
    step: init.step ?? 0,
    targetIds: init.targetIds ?? null,
    productId: init.productId ?? null,
    productPreSelected: init.productPreSelected ?? false,
    campaignName: init.campaignName ?? '',
    nameEditing: false,
    previewTab: 'talk',
    talkTrack: '',
    emailSubject: '',
    emailBody: '',
    talkVariant: 0,
    emailVariant: 0,
    tuneChips: { talk: [], email: [] },
    generating: { talk: false, email: false },
    editing: { talk: false, email: false },
    exportFormat: 'csv',
    draftSaved: false,
  };
}

export type CcAction =
  | { type: 'goTo'; step: number }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'selectProduct'; productId: string }
  | { type: 'clearProduct' }
  | { type: 'setName'; name: string }
  | { type: 'toggleNameEditing' }
  | { type: 'setPreviewTab'; tab: PreviewTab }
  | { type: 'regenerate'; tab: PreviewTab; product: Product }
  | { type: 'addChip'; tab: PreviewTab; chip: string }
  | { type: 'removeChip'; tab: PreviewTab; index: number }
  | { type: 'applyTune'; tab: PreviewTab; product: Product; style: TuneStyle }
  | { type: 'setGenerating'; tab: PreviewTab; value: boolean }
  | { type: 'toggleEditing'; tab: PreviewTab }
  | { type: 'setTalkTrack'; value: string }
  | { type: 'setEmailSubject'; value: string }
  | { type: 'setEmailBody'; value: string }
  | { type: 'setExportFormat'; format: ExportFormat }
  | { type: 'markDraftSaved' }
  | { type: 'regenerateDrafts'; product: Product | null }
  | { type: 'seedForProduct'; product: Product | null };

function regenDrafts(state: CcState, product: Product | null): CcState {
  if (!product) {
    return { ...state, talkTrack: '', emailSubject: '', emailBody: '' };
  }
  const email = generateEmail(product, state.emailVariant, null);
  return {
    ...state,
    talkTrack: generateTalkTrack(product, state.talkVariant, null),
    emailSubject: email.subject,
    emailBody: email.body,
  };
}

export function ccReducer(state: CcState, action: CcAction): CcState {
  switch (action.type) {
    case 'goTo':
      return { ...state, step: Math.max(0, Math.min(CC_STEPS.length - 1, action.step)) };
    case 'next':
      return state.step < CC_STEPS.length - 1 ? { ...state, step: state.step + 1 } : state;
    case 'back':
      return state.step > 0 ? { ...state, step: state.step - 1 } : state;
    case 'selectProduct':
      return { ...state, productId: action.productId };
    case 'clearProduct':
      return { ...state, productId: null };
    case 'setName':
      return { ...state, campaignName: action.name };
    case 'toggleNameEditing':
      return { ...state, nameEditing: !state.nameEditing };
    case 'setPreviewTab':
      return { ...state, previewTab: action.tab };
    case 'regenerate': {
      const variantKey = action.tab === 'talk' ? 'talkVariant' : 'emailVariant';
      const nextVariant = (state[variantKey] + 1) % 3;
      const next = { ...state, [variantKey]: nextVariant };
      if (action.tab === 'talk') {
        next.talkTrack = generateTalkTrack(action.product, nextVariant, null);
      } else {
        const email = generateEmail(action.product, nextVariant, null);
        next.emailSubject = email.subject;
        next.emailBody = email.body;
      }
      return next;
    }
    case 'addChip': {
      const chips = state.tuneChips[action.tab];
      if (chips.includes(action.chip)) return state;
      return {
        ...state,
        tuneChips: { ...state.tuneChips, [action.tab]: [...chips, action.chip] },
      };
    }
    case 'removeChip': {
      const chips = state.tuneChips[action.tab].filter((_, i) => i !== action.index);
      return { ...state, tuneChips: { ...state.tuneChips, [action.tab]: chips } };
    }
    case 'applyTune': {
      // No style keyword → cycle to a fresh variant (Ryan's behavior).
      let next = { ...state, tuneChips: { ...state.tuneChips, [action.tab]: [] } };
      let variant = action.tab === 'talk' ? state.talkVariant : state.emailVariant;
      if (action.style === null) {
        variant = (variant + 1) % 3;
        next =
          action.tab === 'talk'
            ? { ...next, talkVariant: variant }
            : { ...next, emailVariant: variant };
      }
      if (action.tab === 'talk') {
        next.talkTrack = generateTalkTrack(action.product, variant, action.style);
      } else {
        const email = generateEmail(action.product, variant, action.style);
        next.emailSubject = email.subject;
        next.emailBody = email.body;
      }
      return next;
    }
    case 'setGenerating':
      return { ...state, generating: { ...state.generating, [action.tab]: action.value } };
    case 'toggleEditing':
      return { ...state, editing: { ...state.editing, [action.tab]: !state.editing[action.tab] } };
    case 'setTalkTrack':
      return { ...state, talkTrack: action.value };
    case 'setEmailSubject':
      return { ...state, emailSubject: action.value };
    case 'setEmailBody':
      return { ...state, emailBody: action.value };
    case 'setExportFormat':
      return { ...state, exportFormat: action.format };
    case 'markDraftSaved':
      return { ...state, draftSaved: true };
    case 'regenerateDrafts':
      return regenDrafts(state, action.product);
    case 'seedForProduct': {
      // Seed an auto name when none is set, then regenerate drafts. Used by the
      // wizard's product-change effect so it needs no state read in its deps.
      const named =
        action.product && state.campaignName === ''
          ? { ...state, campaignName: suggestCampaignName(action.product) }
          : state;
      return regenDrafts(named, action.product);
    }
  }
}

/**
 * Whether the wizard can advance from the current step (gating).
 * `resolvedTargetCount` is the size of the CURRENT target set (contextual seed
 * or auto-computed) — resolution lives outside the reducer, so gating takes it
 * as an input rather than reading state.
 */
export function canContinue(state: CcState, resolvedTargetCount: number): boolean {
  switch (state.step) {
    case 0:
      return state.productId !== null && resolvedTargetCount > 0; // Product + targets
    case 1:
      return state.campaignName.trim().length > 0; // Messaging (name required)
    default:
      return true; // Preview/export
  }
}

/** The gating hint shown when the current step can't continue. */
export function gatingHint(state: CcState, resolvedTargetCount: number): string {
  switch (state.step) {
    case 0:
      return state.productId === null
        ? 'Pick a product'
        : resolvedTargetCount > 0
          ? ''
          : 'No eligible customers for this product';
    case 1:
      return 'Name your campaign';
    default:
      return '';
  }
}

/** Suggests a default campaign name from product + month. */
export function suggestCampaignName(product: Product | null): string {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const d = new Date();
  const monthYear = `${months[d.getMonth()] ?? ''} ${String(d.getFullYear())}`;
  if (product) return `${product.name} Outreach · ${monthYear}`;
  return `New Campaign · ${monthYear}`;
}

/**
 * Resolves the wizard's target set (SCRUM-500).
 * - Contextual seed (non-null): the seeded customers ARE the target set,
 *   regardless of the selected product — a referral/drawer entry deliberately
 *   targets ITS customer even when a different product gets picked (accepted
 *   behavior; the Product step shows the list so it is always visible).
 * - Auto mode (null seed): the customers passing the selected product's
 *   eligibility rules (the shared forcedAllMet predicate); empty until a
 *   product is chosen.
 */
export function resolveTargets(
  seed: string[] | null,
  product: Product | null,
  customers: Customer[],
): Customer[] {
  if (seed) {
    return customers.filter((c) => seed.includes(c.id));
  }
  return product ? eligibleTargets(customers, product) : [];
}

export interface TargetSnapshot {
  count: number;
  avgBalance: number;
  avgTenureMonths: number;
  personal: number;
  business: number;
  avgHolderAge: number | null;
}

/** Computes the target-set snapshot stats (shown on the Product step). */
export function targetSnapshot(customers: Customer[]): TargetSnapshot {
  const count = customers.length;
  if (count === 0) {
    return {
      count: 0,
      avgBalance: 0,
      avgTenureMonths: 0,
      personal: 0,
      business: 0,
      avgHolderAge: null,
    };
  }
  const personal = customers.filter((c) => c.accountType === 'Personal').length;
  const avgBalance = Math.round(customers.reduce((s, c) => s + c.balance, 0) / count);
  const avgTenureMonths = Math.round(customers.reduce((s, c) => s + c.ageMo, 0) / count);
  return {
    count,
    avgBalance,
    avgTenureMonths,
    personal,
    business: count - personal,
    avgHolderAge: null, // holder-age avg computed in the component (needs the derive util)
  };
}
